import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Lightweight i18n. Two languages: EN and ES. Flat keys, dot-namespaced.
 * Usage: const t = useT(); t("nav.dashboards")
 */

const DICT = {
  en: {
    "common.save": "Save",
    "common.saveChanges": "Save changes",
    "common.cancel": "Cancel",
    "common.create": "Create",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.refresh": "Refresh",
    "common.loading": "Loading…",
    "common.search": "Search",
    "common.copy": "Copy",
    "common.copied": "Copied",
    "common.back": "Back",
    "common.close": "Close",
    "common.actions": "Actions",
    "common.name": "Name",
    "common.description": "Description",
    "common.email": "Email",
    "common.password": "Password",
    "common.role": "Role",
    "common.company": "Company",
    "common.created": "Created",
    "common.status": "Status",
    "common.test": "Test",
    "common.save_settings": "Save settings",
    "common.send": "Send",
    "common.required_name": "Name is required",
    "common.confirm_delete": "Delete?",

    "auth.sign_in": "Sign in",
    "auth.welcome": "Welcome back",
    "auth.subtitle": "Use your email and password to continue.",
    "auth.signing_in": "Signing in…",
    "auth.demo_accounts": "Demo accounts (seeded):",
    "auth.invalid": "Invalid email or password",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.log_out": "Log out",

    "nav.workspace": "Workspace",
    "nav.all_companies": "All companies",
    "nav.my_company": "My company",
    "nav.dashboards": "Dashboards",
    "nav.datasources": "Datasources",
    "nav.alarms": "Alarms",
    "nav.users": "Users",
    "nav.companies": "Companies",
    "nav.smtp": "SMTP",
    "nav.audit": "Audit logs",
    "nav.contact_messages": "Contact inbox",
    "nav.tagline": "industrial analytics",

    "dash.title": "Your dashboards",
    "dash.subtitle_one": "{n} dashboard visible to your account.",
    "dash.subtitle_many": "{n} dashboards visible to your account.",
    "dash.new": "New dashboard",
    "dash.empty_admin": "Create your first dashboard to start visualizing data from your SQL sources.",
    "dash.empty_user": "Your administrator hasn't shared any dashboards with this account yet.",
    "dash.empty_title": "No dashboards yet",
    "dash.duplicate": "Duplicate",
    "dash.export_csv": "Export CSV",
    "dash.export_xlsx": "Export XLSX",
    "dash.export_pdf": "Export PDF",
    "dash.create_dashboard": "Create dashboard",
    "dash.no_description": "No description",
    "dash.confirm_delete": "Delete this dashboard and its charts/alarms?",
    "dash.duplicated": "Dashboard duplicated",
    "dash.export_failed": "Export failed",
    "dash.dialog_title": "New dashboard",
    "dash.dialog_subtitle": "Give your dashboard a name. You can add charts after.",

    "detail.back": "All dashboards",
    "detail.charts_count_one": "· {n} chart",
    "detail.charts_count_many": "· {n} charts",
    "detail.edit_layout": "Edit layout",
    "detail.lock_layout": "Lock layout",
    "detail.save_layout": "Save layout",
    "detail.layout_saved": "Layout saved",
    "detail.add_chart": "Add chart",
    "detail.export": "Export",
    "detail.duplicate": "Duplicate",
    "detail.empty_charts_admin": "Add your first chart by writing a SQL query against one of your datasources.",
    "detail.empty_charts_user": "Your administrator hasn't added charts to this dashboard yet.",
    "detail.empty_title": "No charts yet",
    "detail.edit_mode_hint": "Edit mode — drag headers, resize from corner",
    "detail.loading": "Loading dashboard…",

    "chart.title": "Title",
    "chart.type": "Chart type",
    "chart.line": "Line",
    "chart.area": "Area",
    "chart.bar": "Bar",
    "chart.scatter": "Scatter",
    "chart.datasource": "Datasource",
    "chart.choose": "Choose…",
    "chart.sql_query": "SQL query",
    "chart.x_field": "X field",
    "chart.y_field": "Y field",
    "chart.x_label": "X label",
    "chart.y_label": "Y label",
    "chart.color": "Color",
    "chart.refresh_seconds": "Refresh interval (sec)",
    "chart.new": "New chart",
    "chart.edit": "Edit chart",
    "chart.create_chart": "Create chart",
    "chart.configure_alarm": "Configure alarm",
    "chart.alarm_history": "Alarm history",
    "chart.make_wider": "Make wider",
    "chart.make_narrower": "Make narrower",
    "chart.make_taller": "Make taller",
    "chart.make_shorter": "Make shorter",
    "chart.query_failed": "Query failed",
    "chart.no_data": "No data returned by query.",
    "chart.required": "Title, datasource and SQL query are required",
    "chart.created": "Chart created",
    "chart.updated": "Chart updated",
    "chart.confirm_delete": 'Delete chart "{title}"?',

    "alarm.title": "Threshold alerts",
    "alarm.subtitle":
      "Background scheduler checks every alarm at its configured interval and sends email when triggered.",
    "alarm.no_alarms": "No alarms yet. Create alarms from a chart's menu.",
    "alarm.condition": "Condition",
    "alarm.threshold": "Threshold",
    "alarm.interval": "Check interval",
    "alarm.every_n_min": "Every {n} minutes",
    "alarm.recipients": "Recipient emails (comma-separated)",
    "alarm.last_value": "Last value",
    "alarm.triggered": "Triggered",
    "alarm.ok": "OK",
    "alarm.paused": "Paused",
    "alarm.enabled": "Enabled",
    "alarm.gt": "value > threshold",
    "alarm.lt": "value < threshold",
    "alarm.new": "New alarm",
    "alarm.edit": "Edit alarm",
    "alarm.create_alarm": "Create alarm",
    "alarm.threshold_alert": "Threshold alert",
    "alarm.confirm_delete": "Delete alarm?",
    "alarm.history": "Alarm history",
    "alarm.no_events":
      "No events recorded yet. The alarm will record an entry the first time the threshold is crossed.",
    "alarm.value": "Value",
    "alarm.email_sent": "Email sent",
    "alarm.not_notified": "Not notified",

    "ds.title": "External SQL connections",
    "ds.subtitle": "Connect PostgreSQL, MySQL or SQLite databases. Credentials are encrypted at rest.",
    "ds.new": "New datasource",
    "ds.edit": "Edit datasource",
    "ds.no_datasources": "No datasources yet",
    "ds.host": "Host",
    "ds.port": "Port",
    "ds.database": "Database",
    "ds.file_path": "File path",
    "ds.username": "Username",
    "ds.password_keep": "(leave empty to keep)",
    "ds.kind": "Kind",
    "ds.test_connection": "Test connection",
    "ds.connection_ok": "Connection ok",
    "ds.connection_failed": "Connection failed: {msg}",
    "ds.encrypted": "Credentials are encrypted with AES (Fernet) before storage.",
    "ds.required": "Name, kind and database are required",
    "ds.created": "Datasource created",
    "ds.updated": "Datasource updated",
    "ds.confirm_delete": "Delete datasource?",
    "ds.global": "Global (all companies)",
    "ds.optional_company": "Company (optional)",

    "schema.title": "Schema explorer",
    "schema.filter": "Filter tables / columns…",
    "schema.no_match": "No tables match.",
    "schema.no_data": "No data.",
    "schema.starter": "Starter SQL",
    "schema.cols": "{n} cols",
    "schema.loading": "Loading schema…",

    "user.title": "Team members",
    "user.subtitle": "Admins manage everything. Users are scoped to their company.",
    "user.new": "New user",
    "user.edit": "Edit user",
    "user.no_users": "No users",
    "user.required_email_name": "Email and name required",
    "user.required_password": "Password required",
    "user.required_company": "Regular users need a company",
    "user.confirm_delete": "Delete user?",
    "user.dialog_subtitle": "Email/password sign-in. Roles: admin or user.",
    "user.role_admin": "Admin",
    "user.role_user": "User",

    "company.title": "Tenants",
    "company.subtitle":
      "Each company sees only its own dashboards, charts, datasources and users.",
    "company.new": "New company",
    "company.edit": "Edit company",
    "company.no_companies": "No companies yet",
    "company.confirm_delete": "Delete company and all its data?",
    "company.dialog_subtitle": "Tenant scope for users, dashboards and datasources.",

    "smtp.title": "Email configuration",
    "smtp.subtitle":
      "Used to deliver alarm notifications. Password is encrypted at rest.",
    "smtp.server_settings": "Server settings",
    "smtp.from_email": "From email",
    "smtp.host_required": "Host required",
    "smtp.starttls": "STARTTLS",
    "smtp.starttls_hint": "Most providers (port 587)",
    "smtp.implicit_ssl": "Implicit SSL",
    "smtp.implicit_ssl_hint": "Port 465",
    "smtp.send_test_title": "Send test email",
    "smtp.send_test": "Send test",
    "smtp.test_recipient_required": "Recipient required",
    "smtp.saved": "SMTP saved",
    "smtp.test_sent": "Test email sent",

    "audit.title": "Activity",
    "audit.subtitle": "Every privileged action is recorded. Last 300 events shown.",
    "audit.no_events": "No activity yet",
    "audit.time": "Time",
    "audit.actor": "Actor",
    "audit.action": "Action",
    "audit.target": "Target",
    "audit.details": "Details",

    "lang.label": "Language",
  },
  es: {
    "common.save": "Guardar",
    "common.saveChanges": "Guardar cambios",
    "common.cancel": "Cancelar",
    "common.create": "Crear",
    "common.delete": "Eliminar",
    "common.edit": "Editar",
    "common.refresh": "Actualizar",
    "common.loading": "Cargando…",
    "common.search": "Buscar",
    "common.copy": "Copiar",
    "common.copied": "Copiado",
    "common.back": "Volver",
    "common.close": "Cerrar",
    "common.actions": "Acciones",
    "common.name": "Nombre",
    "common.description": "Descripción",
    "common.email": "Correo electrónico",
    "common.password": "Contraseña",
    "common.role": "Rol",
    "common.company": "Empresa",
    "common.created": "Creado",
    "common.status": "Estado",
    "common.test": "Probar",
    "common.save_settings": "Guardar configuración",
    "common.send": "Enviar",
    "common.required_name": "El nombre es obligatorio",
    "common.confirm_delete": "¿Eliminar?",

    "auth.sign_in": "Iniciar sesión",
    "auth.welcome": "Bienvenido de nuevo",
    "auth.subtitle": "Usa tu correo y contraseña para continuar.",
    "auth.signing_in": "Iniciando sesión…",
    "auth.demo_accounts": "Cuentas de demostración:",
    "auth.invalid": "Correo o contraseña incorrectos",
    "auth.email": "Correo electrónico",
    "auth.password": "Contraseña",
    "auth.log_out": "Cerrar sesión",

    "nav.workspace": "Espacio",
    "nav.all_companies": "Todas las empresas",
    "nav.my_company": "Mi empresa",
    "nav.dashboards": "Tableros",
    "nav.datasources": "Orígenes de datos",
    "nav.alarms": "Alarmas",
    "nav.users": "Usuarios",
    "nav.companies": "Empresas",
    "nav.smtp": "SMTP",
    "nav.audit": "Auditoría",
    "nav.contact_messages": "Bandeja de contactos",
    "nav.tagline": "analítica industrial",

    "dash.title": "Tus tableros",
    "dash.subtitle_one": "{n} tablero visible para tu cuenta.",
    "dash.subtitle_many": "{n} tableros visibles para tu cuenta.",
    "dash.new": "Nuevo tablero",
    "dash.empty_admin":
      "Crea tu primer tablero para empezar a visualizar datos de tus orígenes SQL.",
    "dash.empty_user":
      "Tu administrador aún no ha compartido tableros con esta cuenta.",
    "dash.empty_title": "Aún no hay tableros",
    "dash.duplicate": "Duplicar",
    "dash.export_csv": "Exportar CSV",
    "dash.export_xlsx": "Exportar XLSX",
    "dash.export_pdf": "Exportar PDF",
    "dash.create_dashboard": "Crear tablero",
    "dash.no_description": "Sin descripción",
    "dash.confirm_delete": "¿Eliminar este tablero, sus gráficos y alarmas?",
    "dash.duplicated": "Tablero duplicado",
    "dash.export_failed": "Falló la exportación",
    "dash.dialog_title": "Nuevo tablero",
    "dash.dialog_subtitle":
      "Asigna un nombre. Podrás añadir gráficos a continuación.",

    "detail.back": "Todos los tableros",
    "detail.charts_count_one": "· {n} gráfico",
    "detail.charts_count_many": "· {n} gráficos",
    "detail.edit_layout": "Editar diseño",
    "detail.lock_layout": "Bloquear diseño",
    "detail.save_layout": "Guardar diseño",
    "detail.layout_saved": "Diseño guardado",
    "detail.add_chart": "Añadir gráfico",
    "detail.export": "Exportar",
    "detail.duplicate": "Duplicar",
    "detail.empty_charts_admin":
      "Añade tu primer gráfico escribiendo una consulta SQL contra uno de tus orígenes de datos.",
    "detail.empty_charts_user":
      "Tu administrador aún no ha añadido gráficos a este tablero.",
    "detail.empty_title": "Aún no hay gráficos",
    "detail.edit_mode_hint":
      "Modo edición — arrastra los encabezados, redimensiona desde la esquina",
    "detail.loading": "Cargando tablero…",

    "chart.title": "Título",
    "chart.type": "Tipo de gráfico",
    "chart.line": "Líneas",
    "chart.area": "Área",
    "chart.bar": "Barras",
    "chart.scatter": "Dispersión",
    "chart.datasource": "Origen de datos",
    "chart.choose": "Selecciona…",
    "chart.sql_query": "Consulta SQL",
    "chart.x_field": "Campo X",
    "chart.y_field": "Campo Y",
    "chart.x_label": "Etiqueta X",
    "chart.y_label": "Etiqueta Y",
    "chart.color": "Color",
    "chart.refresh_seconds": "Intervalo de actualización (seg)",
    "chart.new": "Nuevo gráfico",
    "chart.edit": "Editar gráfico",
    "chart.create_chart": "Crear gráfico",
    "chart.configure_alarm": "Configurar alarma",
    "chart.alarm_history": "Historial de alarma",
    "chart.make_wider": "Hacer más ancho",
    "chart.make_narrower": "Hacer más estrecho",
    "chart.make_taller": "Hacer más alto",
    "chart.make_shorter": "Hacer más bajo",
    "chart.query_failed": "Falló la consulta",
    "chart.no_data": "La consulta no devolvió datos.",
    "chart.required":
      "Título, origen de datos y consulta SQL son obligatorios",
    "chart.created": "Gráfico creado",
    "chart.updated": "Gráfico actualizado",
    "chart.confirm_delete": '¿Eliminar el gráfico "{title}"?',

    "alarm.title": "Alertas por umbral",
    "alarm.subtitle":
      "El planificador en segundo plano comprueba cada alarma según su intervalo y envía un correo cuando se dispara.",
    "alarm.no_alarms":
      "Aún no hay alarmas. Crea alarmas desde el menú de un gráfico.",
    "alarm.condition": "Condición",
    "alarm.threshold": "Umbral",
    "alarm.interval": "Intervalo de revisión",
    "alarm.every_n_min": "Cada {n} minutos",
    "alarm.recipients":
      "Correos destinatarios (separados por coma)",
    "alarm.last_value": "Último valor",
    "alarm.triggered": "Disparada",
    "alarm.ok": "OK",
    "alarm.paused": "Pausada",
    "alarm.enabled": "Habilitada",
    "alarm.gt": "valor > umbral",
    "alarm.lt": "valor < umbral",
    "alarm.new": "Nueva alarma",
    "alarm.edit": "Editar alarma",
    "alarm.create_alarm": "Crear alarma",
    "alarm.threshold_alert": "Alerta por umbral",
    "alarm.confirm_delete": "¿Eliminar alarma?",
    "alarm.history": "Historial de alarma",
    "alarm.no_events":
      "Aún no se ha registrado ningún evento. La alarma registrará un evento la primera vez que se cruce el umbral.",
    "alarm.value": "Valor",
    "alarm.email_sent": "Correo enviado",
    "alarm.not_notified": "Sin notificar",

    "ds.title": "Conexiones SQL externas",
    "ds.subtitle":
      "Conecta bases de datos PostgreSQL, MySQL o SQLite. Las credenciales se cifran en reposo.",
    "ds.new": "Nuevo origen de datos",
    "ds.edit": "Editar origen de datos",
    "ds.no_datasources": "Aún no hay orígenes de datos",
    "ds.host": "Host",
    "ds.port": "Puerto",
    "ds.database": "Base de datos",
    "ds.file_path": "Ruta del archivo",
    "ds.username": "Usuario",
    "ds.password_keep": "(déjalo vacío para mantenerlo)",
    "ds.kind": "Tipo",
    "ds.test_connection": "Probar conexión",
    "ds.connection_ok": "Conexión correcta",
    "ds.connection_failed": "Falló la conexión: {msg}",
    "ds.encrypted":
      "Las credenciales se cifran con AES (Fernet) antes de almacenarse.",
    "ds.required": "Nombre, tipo y base de datos son obligatorios",
    "ds.created": "Origen de datos creado",
    "ds.updated": "Origen de datos actualizado",
    "ds.confirm_delete": "¿Eliminar origen de datos?",
    "ds.global": "Global (todas las empresas)",
    "ds.optional_company": "Empresa (opcional)",

    "schema.title": "Explorador de esquema",
    "schema.filter": "Filtrar tablas / columnas…",
    "schema.no_match": "Ninguna tabla coincide.",
    "schema.no_data": "Sin datos.",
    "schema.starter": "SQL inicial",
    "schema.cols": "{n} cols",
    "schema.loading": "Cargando esquema…",

    "user.title": "Miembros del equipo",
    "user.subtitle":
      "Los administradores gestionan todo. Los usuarios están limitados a su empresa.",
    "user.new": "Nuevo usuario",
    "user.edit": "Editar usuario",
    "user.no_users": "Sin usuarios",
    "user.required_email_name": "Correo y nombre obligatorios",
    "user.required_password": "Contraseña obligatoria",
    "user.required_company":
      "Los usuarios estándar deben tener una empresa",
    "user.confirm_delete": "¿Eliminar usuario?",
    "user.dialog_subtitle":
      "Inicio de sesión con correo y contraseña. Roles: admin o usuario.",
    "user.role_admin": "Administrador",
    "user.role_user": "Usuario",

    "company.title": "Inquilinos",
    "company.subtitle":
      "Cada empresa solo ve sus propios tableros, gráficos, orígenes de datos y usuarios.",
    "company.new": "Nueva empresa",
    "company.edit": "Editar empresa",
    "company.no_companies": "Aún no hay empresas",
    "company.confirm_delete": "¿Eliminar la empresa y todos sus datos?",
    "company.dialog_subtitle":
      "Ámbito del inquilino para usuarios, tableros y orígenes de datos.",

    "smtp.title": "Configuración de correo",
    "smtp.subtitle":
      "Se usa para enviar las notificaciones de alarma. La contraseña se cifra en reposo.",
    "smtp.server_settings": "Ajustes del servidor",
    "smtp.from_email": "Correo remitente",
    "smtp.host_required": "Host obligatorio",
    "smtp.starttls": "STARTTLS",
    "smtp.starttls_hint": "La mayoría de proveedores (puerto 587)",
    "smtp.implicit_ssl": "SSL implícito",
    "smtp.implicit_ssl_hint": "Puerto 465",
    "smtp.send_test_title": "Enviar correo de prueba",
    "smtp.send_test": "Enviar prueba",
    "smtp.test_recipient_required": "Destinatario obligatorio",
    "smtp.saved": "Configuración SMTP guardada",
    "smtp.test_sent": "Correo de prueba enviado",

    "audit.title": "Actividad",
    "audit.subtitle":
      "Cada acción privilegiada queda registrada. Se muestran los últimos 300 eventos.",
    "audit.no_events": "Aún no hay actividad",
    "audit.time": "Hora",
    "audit.actor": "Autor",
    "audit.action": "Acción",
    "audit.target": "Objetivo",
    "audit.details": "Detalles",

    "lang.label": "Idioma",
  },
};

const I18nCtx = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

function pluralize(key, n, dict) {
  if (n === 1) {
    const oneKey = key + "_one";
    if (dict[oneKey]) return dict[oneKey];
  } else {
    const manyKey = key + "_many";
    if (dict[manyKey]) return dict[manyKey];
  }
  return dict[key] || key;
}

function interpolate(template, params) {
  if (!template || typeof template !== "string") return template;
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (m, k) => (params[k] != null ? String(params[k]) : m));
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("pb_lang");
    if (saved === "en" || saved === "es") return saved;
    const browser = (navigator.language || "en").toLowerCase().slice(0, 2);
    return browser === "es" ? "es" : "en";
  });

  useEffect(() => {
    localStorage.setItem("pb_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key, params) => {
      const dict = DICT[lang] || DICT.en;
      let value;
      if (params && typeof params.count === "number") {
        value = pluralize(key, params.count, dict);
      } else {
        value = dict[key] || DICT.en[key] || key;
      }
      return interpolate(value, params);
    },
    [lang]
  );

  // Memoize Provider value so consumers don't re-render on every parent render.
  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  return useContext(I18nCtx);
}

export function useT() {
  return useContext(I18nCtx).t;
}
